import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Icon from "@/components/base/Icon";
import { useSeo } from "@/hooks/useSeo";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db74rd7799gd4v2fec00";

const SERVICE_ITEMS = [
  {
    icon: "ri-book-2-line",
    title: "海量分类浏览",
    desc: "按恋爱、热血、校园、奇幻等维度组织内容，快速找到你想看的口味。",
  },
  {
    icon: "ri-list-check-2",
    title: "章节预览与追更",
    desc: "章节目录最新优先，连载进度一目了然，喜欢的作品随时回来接着读。",
  },
  {
    icon: "ri-device-line",
    title: "多设备舒适阅读",
    desc: "阅读页自适应手机与电脑，支持字号与背景色调节，减少眼睛疲劳。",
  },
  {
    icon: "ri-magic-line",
    title: "个性化推荐",
    desc: "基于分类与标签展示相关推荐，让发现下一部心动漫画更简单。",
  },
];

const NOTICE_ITEMS = [
  "本站为漫画阅读体验演示站点，站内漫画图片、章节与数据均为演示内容，不代表真实作品。",
  "搜索、筛选、分页与推荐为前端模拟实现，后续可接入真实数据接口。",
  "收藏与阅读设置仅保存在你本机浏览器，不涉及任何账号体系与个人信息上传。",
  "本站内容面向 18 岁以上读者，请合理安排阅读时间，理性阅读。",
];

const SUBJECT_OPTIONS = ["内容反馈", "使用问题", "作品推荐", "合作建议", "版权相关"];

export default function AboutPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  useSeo({
    title: "关于我们－羞羞色漫在线阅读平台",
    description:
      "了解羞羞色漫的定位、服务范围与联系方式，并提交你的反馈建议。羞羞色漫专注为漫画爱好者提供舒适的在线阅读体验。",
    keywords: "羞羞色漫,关于我们,联系方式,意见反馈,漫画平台",
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setErrorMessage("");
      form.reset();
      return;
    }

    setStatus("submitting");
    setErrorMessage("");

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key === "website_alt") return;
        if (typeof value === "string" && value.trim() !== "") {
          body.append(key, value);
        }
      });

      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");
      const failed =
        !response.ok || (parsed != null && parsed.code !== "OK") || isSpam;

      if (failed) {
        setStatus("error");
        setErrorMessage(serverMsg || "提交失败，请稍后重试");
        return;
      }

      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setErrorMessage("网络异常，提交失败，请稍后重试");
    }
  };

  return (
    <div className="w-full px-4 md:px-8 py-6">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

      <header className="mt-6 rounded-lg border border-background-200 bg-secondary-50 p-6 md:p-10">
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
          <Icon name="ri-book-open-fill" className="w-3.5 h-3.5" />
          羞羞色漫
        </span>
        <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">
          为漫画爱好者打造的精品在线阅读平台
        </h1>
        <p className="mt-4 text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
          羞羞色漫定位为精品漫画在线阅读平台，面向 18 岁以上喜欢二次元及各类漫画作品的读者，
          致力于提供海量漫画分类浏览、章节预览、连载更新提示与相关推荐功能，
          让每一位读者都能用简洁直观的方式，快速找到并舒适地读完感兴趣的内容。
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to="/category/all"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors hover:bg-primary-600"
          >
            <Icon name="ri-compass-3-line" className="w-4 h-4" />
            开始探索漫画
          </Link>
          <Link
            to="/search"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md border border-background-300 text-foreground-700 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            <Icon name="ri-search-line" className="w-4 h-4" />
            搜索作品
          </Link>
        </div>
      </header>

      <section className="mt-10">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
          我们提供什么
        </h2>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {SERVICE_ITEMS.map((item) => (
            <div
              key={item.title}
              className="rounded-lg border border-background-200 bg-background-50 p-5"
            >
              <span className="w-10 h-10 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center">
                <Icon name={item.icon} className="w-5 h-5" />
              </span>
              <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                {item.title}
              </h3>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10 grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-6 lg:gap-8 items-start">
        <div className="flex flex-col gap-5">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950">联系方式</h2>
            <p className="mt-2 text-sm text-foreground-600">
              如有内容反馈、作品推荐或合作意向，欢迎通过以下方式联系我们。
            </p>
            <ul className="mt-4 flex flex-col gap-3 text-sm">
              <li className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center">
                  <Icon name="ri-mail-line" className="w-4 h-4" />
                </span>
                <span className="text-foreground-700">
                  邮箱：<strong className="text-foreground-900">support@shame-comic.demo</strong>
                </span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center">
                  <Icon name="ri-time-line" className="w-4 h-4" />
                </span>
                <span className="text-foreground-700">反馈处理时间：工作日 10:00 - 18:00</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center">
                  <Icon name="ri-map-pin-line" className="w-4 h-4" />
                </span>
                <span className="text-foreground-700">线上平台 · 无实体门店</span>
              </li>
            </ul>
          </div>

          <div className="rounded-lg border border-background-200 bg-accent-50 p-5 md:p-6">
            <h2 className="font-heading text-lg font-bold text-accent-950 flex items-center gap-2">
              <Icon name="ri-error-warning-line" className="w-5 h-5" />
              使用须知
            </h2>
            <ul className="mt-3 flex flex-col gap-2.5">
              {NOTICE_ITEMS.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-accent-900 leading-relaxed">
                  <Icon name="ri-checkbox-circle-line" className="w-4 h-4 mt-0.5 shrink-0 text-accent-600" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
          <h2 className="font-heading text-lg font-bold text-foreground-950">意见反馈</h2>
          <p className="mt-2 text-sm text-foreground-600">
            填写下面的表单告诉我们你的想法，我们会认真阅读每一条建议。
          </p>

          {status === "success" ? (
            <div className="mt-5 rounded-lg border border-primary-200 bg-primary-50 p-6 text-center">
              <Icon name="ri-checkbox-circle-fill" className="w-12 h-12 text-primary-500 mx-auto" />
              <p className="mt-3 font-heading text-base font-semibold text-foreground-950">
                感谢你的反馈！
              </p>
              <p className="mt-1 text-sm text-foreground-600">
                我们已收到你的建议，会尽快查看处理。
              </p>
              <button
                type="button"
                onClick={() => setStatus("idle")}
                className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm cursor-pointer transition-colors hover:bg-primary-600"
              >
                再次填写
              </button>
            </div>
          ) : (
            <form
              id="feedback-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="mt-5 flex flex-col gap-4"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="feedback-name" className="block text-sm text-foreground-700 mb-1.5">
                    昵称
                  </label>
                  <input
                    id="feedback-name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入你的昵称"
                    className="w-full h-10 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 cursor-text focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="feedback-email" className="block text-sm text-foreground-700 mb-1.5">
                    邮箱
                  </label>
                  <input
                    id="feedback-email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于接收回复"
                    className="w-full h-10 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 cursor-text focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="feedback-subject" className="block text-sm text-foreground-700 mb-1.5">
                  反馈类型
                </label>
                <select
                  id="feedback-subject"
                  name="subject"
                  defaultValue={SUBJECT_OPTIONS[0]}
                  className="w-full h-10 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 cursor-pointer focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                >
                  {SUBJECT_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="feedback-message" className="block text-sm text-foreground-700 mb-1.5">
                  反馈内容
                </label>
                <textarea
                  id="feedback-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请描述你的建议或遇到的问题（最多 500 字）"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 cursor-text resize-none focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                />
              </div>

              <input
                className="field-guard"
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              {status === "error" && (
                <p className="text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-3 py-2">
                  {errorMessage}
                </p>
              )}

              <button
                type="submit"
                disabled={status === "submitting"}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Icon name="ri-send-plane-line" className="w-4 h-4" />
                {status === "submitting" ? "提交中..." : "提交反馈"}
              </button>
              <p className="text-[11px] text-foreground-400">
                演示站点的反馈表单会真实收集你的留言，请勿填写敏感信息。
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}